import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";

export default function SOPManagement() {
  return (
    <>
      <SEO
        title="SOP Management for Regulated Labs | LabComplyx"
        description="Improve SOP consistency, reduce stale documentation, and streamline SOP lifecycle management with governed workflows, human sign-off, and full audit traceability from LabComplyx."
        canonical="https://labcomplyx.com/solutions/sop-management"
        breadcrumbs={[
          { name: "Home", url: "https://labcomplyx.com/" },
          { name: "Solutions", url: "https://labcomplyx.com/solutions" },
          { name: "SOP Management", url: "https://labcomplyx.com/solutions/sop-management" },
        ]}
      />
      <div className="pt-24">
        <section className="pb-16 bg-background">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 max-w-2xl">
            <span className="inline-block text-xs font-semibold text-secondary bg-secondary/10 px-3 py-1 rounded-full mb-4">Solution</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-5">SOP management that keeps labs inspection-ready</h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              LabComplyx helps regulated labs create, review, version, and manage SOPs with consistency — reducing stale documentation and closing the gap that inspectors find first.
            </p>
            <Link to="/ValidationIntake"><Button size="lg" className="gap-2">Start with a Free SOP Gap Review <ArrowRight className="w-4 h-4" /></Button></Link>
          </div>
        </section>

        <section className="py-16 bg-muted/30 border-y border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-5">Why SOP gaps are the most common citation</h2>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">SOPs that are outdated, inconsistent, or missing from critical workflows are the first thing inspectors flag. Lean QA teams struggle to keep up with the volume and complexity of documentation requirements.</p>
                <ul className="space-y-2.5">
                  {["Outdated SOPs that reflect old equipment or workflows", "Missing version history and review records", "Inconsistent format across departments", "No clear ownership or review schedule", "SOPs that don't match actual lab practice"].map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-muted-foreground"><span className="w-1.5 h-1.5 rounded-full bg-destructive shrink-0 mt-1.5" />{b}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-card border border-border rounded-xl p-7">
                <h3 className="font-semibold text-foreground mb-4">LabComplyx SOP management approach</h3>
                <ul className="space-y-2.5">
                  {["Governed SOP drafting with regulatory alignment", "Structured review and version control workflows", "Human sign-off required before any SOP is finalized", "Full audit trail for creation, review, and approval", "Lifecycle tracking to flag overdue reviews"].map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-muted-foreground"><Check className="w-4 h-4 text-secondary shrink-0 mt-0.5" />{b}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}